// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { toPublicURL } from '@plone/volto/helpers/Url/Url';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import universityModifier from './modifiers/University';
import documentModifier from './modifiers/Document';
import degreeProgramModifier from './modifiers/DegreeProgram';
import occasionModifier from './modifiers/Occasion';
import continuingEducationModifier from './modifiers/ContinuingEducation';
import courseModifier from './modifiers/Course';
import articleModifier from './modifiers/Article';
import eventSeriesModifier from './modifiers/EventSeries';
import projectModifier from './modifiers/Project';
import researchFocusModifier from './modifiers/ResearchFocus';
import topicModifier from './modifiers/Topic';
import contactModifier from './modifiers/Contact';
import teamContactModifier from './modifiers/TeamContact';
import { generateFaqPage } from './faq';
import { SchemaOrgProps, SchemaModifier, LDSchema, Schema } from './types';

const modifiers: Record<string, SchemaModifier> = {
  // Content-type specific modifiers
  University: universityModifier,
  Document: documentModifier,
  DegreeProgram: degreeProgramModifier,
  Occasion: occasionModifier,
  ContinuingEducation: continuingEducationModifier,
  Project: projectModifier,
  ResearchFocus: researchFocusModifier,
  Topic: topicModifier,
  Contact: contactModifier,
  TeamContact: teamContactModifier,

  // Schema.org type specific modifiers
  Course: courseModifier,
  Article: articleModifier,
  EventSeries: eventSeriesModifier,
};

const types: Record<string, string> = {
  InfoEvent: 'EventSeries',
  FHNWEvent: 'EventSeries',
  FHNWNews: 'Article',
  University: 'CollegeOrUniversity',
  DegreeProgram: 'Course',
  ContinuingEducation: 'Course',
  Contact: 'Person',
  TeamContact: 'ContactPoint',
  Service: 'Service',
  Project: 'ResearchProject',
  ResearchArea: 'DefinedTerm',
  ResearchFocus: 'DefinedTerm',
  Topic: 'DefinedTerm',
};

export const generateSchema = ({
  type,
  content,
  state,
  partial = false,
}: SchemaOrgProps): LDSchema => {
  if (!type || !content) {
    return [];
  }

  if (typeof content === 'string') {
    return {
      '@type': types?.[type] || 'WebPage',
      identifier: content,
    };
  }

  const jsonLd: Record<string, any> = {
    '@type': types?.[type] || 'WebPage',
    url: toPublicURL(content['@id']),
    headline: content.title,
    name: content.title,
    identifier: toPublicURL(content['@id']),
  };

  if (!partial) {
    jsonLd['@context'] = 'https://schema.org';
  }

  if (content?.language) {
    jsonLd['inLanguage'] = normalizeContent(content.language);
  }

  if (content?.effective || content?.created) {
    jsonLd['datePublished'] = content.effective || content.created;
  }

  if (content?.modified) {
    jsonLd['dateModified'] = content.modified;
  }

  if (content?.description) {
    jsonLd['description'] = content.description;
  }

  if (
    !partial &&
    content?.preview_image_link &&
    content?.preview_image_link?.image_scales?.image?.[0]
  ) {
    jsonLd['image'] = toPublicURL(
      content.preview_image_link['@id'] +
        '/' +
        content.preview_image_link.image_scales.image[0].download,
    );
  }

  if (content?.['@components']?.university && type !== 'University') {
    const university: Content = content?.['@components']?.university;
    jsonLd['provider'] = generateSchema({
      type: university['@type'],
      content: university,
      state,
      partial: true,
    });
  }

  const jsonLdParts: Schema[] = [jsonLd];

  applyModifiers(content, jsonLd, jsonLdParts, state, partial);

  const faqPage = partial ? null : generateFaqPage(content);

  if (faqPage) {
    jsonLdParts.push(faqPage);
  }

  if (jsonLdParts.length === 1) {
    return jsonLdParts[0];
  }

  return jsonLdParts;
};

export const applyModifiers: SchemaModifier = (
  content,
  main,
  parts,
  state,
  partial,
) => {
  const type = content?.['@type'];
  if (type && modifiers[type]) {
    modifiers[type](content, main, parts, state, partial);
  }

  if (main?.['@type'] && modifiers[main['@type'] as string]) {
    modifiers[main['@type'] as string](content, main, parts, state, partial);
  }
};
